import { useEffect, useState, useCallback } from 'react'
import { isEqual } from 'lodash-es'
import { isJSON } from 'utils/helper'

const NonParsableKeys = ['token', 'LANGUAGE_SELECTION', 'isLoginMessageCleared']

const appVersion = process.env.REACT_APP_VERSION ?? 'default'
export const USE_LOCAL_STORAGE_KEY = 'use-local-storage'
export const USE_LOCAL_STORAGE_PERSIST_KEY = `${USE_LOCAL_STORAGE_KEY}:${appVersion}`

const useLocalStorage = <T>(
  key: string,
  initialValue: T,
  setToString = true,
): [T, (value: T | ((previous: T) => T)) => void] => {
  // Get from local storage then
  // parse stored json or return initialValue
  const readValue = useCallback(() => {
    // Prevent build error "window is undefined" but keep keep working
    if (typeof window === 'undefined') {
      return initialValue
    }

    try {
      const entry = window.localStorage.getItem(USE_LOCAL_STORAGE_PERSIST_KEY)
      const item = entry ? JSON.parse(entry)[key] : null

      if (!item) {
        return initialValue
      }

      if (NonParsableKeys.includes(USE_LOCAL_STORAGE_PERSIST_KEY)) {
        return item
      }

      return isJSON(item) ? JSON.parse(item) : item
    } catch (error) {
      console.warn(`Error reading localStorage key “${USE_LOCAL_STORAGE_PERSIST_KEY}”:`, error)
      return window.localStorage.getItem(USE_LOCAL_STORAGE_PERSIST_KEY) ?? initialValue
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [USE_LOCAL_STORAGE_PERSIST_KEY, key])

  const [storedValue, setStoredValue] = useState<T>(readValue())

  const setValue = useCallback(
    (value: T) => {
      // Prevent build error "window is undefined" but keep keep working
      if (typeof window == 'undefined') {
        console.warn(
          `Tried setting localStorage key “${USE_LOCAL_STORAGE_PERSIST_KEY}” even though environment is not a client`,
        )
      }

      try {
        const entry = localStorage.getItem(USE_LOCAL_STORAGE_PERSIST_KEY)
        const currentData = entry && isJSON(entry) ? JSON.parse(entry) : {}

        // Allow value to be a function so we have the same API as useState
        const newValue = value instanceof Function ? value(storedValue) : value

        const newData = {
          ...currentData,
          [key]: setToString ? JSON.stringify(newValue) : newValue,
        }

        // Save to local storage
        window.localStorage.setItem(USE_LOCAL_STORAGE_PERSIST_KEY, JSON.stringify(newData))

        // Save state
        setStoredValue(newValue)

        // We dispatch a custom event so every useLocalStorage hook are notified
        window.dispatchEvent(new Event('local-storage'))
      } catch (error) {
        console.warn(`Error setting localStorage key “${USE_LOCAL_STORAGE_PERSIST_KEY}”:`, error)
      }
    },
    [storedValue, key, setToString],
  )

  const setLocalStorage: (value: T | ((previous: T) => T)) => void = useCallback(
    (val) => {
      if (typeof val === 'function') {
        setValue((val as (previous: T) => T)(storedValue))
        return
      }

      setValue(val)
    },
    [setValue, storedValue],
  )

  // Update stored value on key change
  useEffect(() => {
    setStoredValue(readValue())
  }, [readValue])

  // Add event listeners to listen to local storage change
  useEffect(() => {
    const handleStorageChange = () => {
      setStoredValue((prev) => {
        const newValue = readValue()

        if (isEqual(newValue, prev)) {
          return prev
        }

        return newValue
      })
    }

    // this only works for other documents, not the current one
    window.addEventListener('storage', handleStorageChange)

    // this is a custom event, triggered in writeValueToLocalStorage
    window.addEventListener('local-storage', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('local-storage', handleStorageChange)
    }
  }, [readValue])

  return [storedValue, setLocalStorage]
}

export const clearUseLocalStorage = () => {
  window.localStorage.removeItem(USE_LOCAL_STORAGE_PERSIST_KEY)
}

export default useLocalStorage
